import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ROUTES } from '../Constants/apiRoutes'; // Update path if needed
import { fetchUserProfile } from '../Auth/authSlice';
import { auth } from '../../firebase';
import { showFeedback } from '../UI/uiFeedbackSlice'; 


// 🔹 Load available plans (public)
export const fetchPlans = createAsyncThunk(
  'subscription/fetchPlans',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(API_ROUTES.GET_PLANS);
      // backend already returns array (you said: "NOT response.data.plans")
      return response.data?.data || [];
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to load subscription plans';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load plans',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);


// 🔹 Start/handle subscription via Razorpay (awaits checkout)
export const subscribeToPlan = createAsyncThunk(
  'subscription/subscribeToPlan',
  async ({ planId, userToken }, { dispatch, rejectWithValue }) => {
    try {
      // 1) Create subscription on backend
      const { data } = await axios.post(
        API_ROUTES.CREATE_SUBSCRIPTION,
        { planId },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );

      const subscriptionId = data.subscriptionId || data.razorpaySubscriptionId;
      const keyId = data.keyId;

      if (!subscriptionId || !keyId) {
        throw new Error('Missing subscriptionId / keyId from server');
      }

      // 2) Ensure Razorpay script exists
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
          document.body.appendChild(s);
        });
      }

      // 3) Open Razorpay and WAIT until user completes/cancels/fails
      const result = await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: keyId,
          name: 'Site360',
          description: 'Site360 Pro Subscription',
          subscription_id: subscriptionId,
          prefill: {
            email: auth.currentUser?.email || undefined,
          },
          theme: { color: '#F37254' },

          // Payment success callback (still not final truth)
          handler: () => {
            dispatch(
              showFeedback({
                type: 'info',
                title: 'Payment received',
                message: 'Verifying your subscription…',
              })
            );
            resolve({ status: 'paid' }); // BillingTab will start polling
          },

          modal: {
            ondismiss: () => {
              dispatch(
                showFeedback({
                  type: 'info',
                  title: 'Checkout cancelled',
                  message: 'No payment was completed.',
                })
              );
              resolve({ status: 'cancelled' });
            },
          },
        });

        // Payment failed event
        razorpay.on('payment.failed', (resp) => {
          const msg =
            resp?.error?.description ||
            resp?.error?.reason ||
            'Payment could not be completed.';
          dispatch(
            showFeedback({
              type: 'error',
              title: 'Payment failed',
              message: msg,
            })
          );
          reject(new Error(msg));
        });

        try {
          razorpay.open();
        } catch (e) {
          reject(e);
        }
      });

      return result; // { status: 'paid' | 'cancelled' }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        'Subscription failed';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Subscription failed',
          message: msg,
        })
      );

      return rejectWithValue(msg);
    }
  }
);





const initialState = {
  allPlans: [],
  currentPlan: null, // 'Free', 'Pro', etc.
  loading: false,
  error: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setCurrentPlan: (state, action) => {
      state.currentPlan = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Plans
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.allPlans = action.payload || [];
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || null;
      })



       // Subscribe
      .addCase(subscribeToPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(subscribeToPlan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(subscribeToPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || null;
      })
   

      // When auth slice refreshes the profile, sync currentPlan
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.currentPlan = action.payload?.plan?.toLowerCase?.() || 'free';
      });



  },
});



export const { setCurrentPlan } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
